// src/whatsappChat/components/TemplatePreview.jsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faVideo, faFile, faLocationDot } from '@fortawesome/free-solid-svg-icons';

const MEDIA_ICONS = { IMAGE: faImage, VIDEO: faVideo, DOCUMENT: faFile, LOCATION: faLocationDot };

// WhatsApp text markers. Each pattern captures (what precedes the marker)(the marked text).
const WA_FORMATS = [
  { re: /()```([\s\S]+?)```/, tag: 'code', className: 'wa-fmt-mono' },
  { re: /()`([^`\n]+)`/, tag: 'code', className: 'wa-fmt-code' },
  { re: /(^|[^A-Za-z0-9*])\*(\S(?:[^*\n]*\S)?)\*(?![A-Za-z0-9])/, tag: 'strong', nested: true },
  { re: /(^|[^A-Za-z0-9_])_(\S(?:[^_\n]*\S)?)_(?![A-Za-z0-9])/, tag: 'em', nested: true },
  { re: /(^|[^A-Za-z0-9~])~(\S(?:[^~\n]*\S)?)~(?![A-Za-z0-9])/, tag: 's', nested: true },
];

/** Renders *bold*, _italic_, ~strike~, `code` and ```monospace``` the way WhatsApp does. */
export function formatWaText(text) {
  const nodes = [];
  let rest = String(text || '');
  while (rest) {
    let best = null;
    for (const format of WA_FORMATS) {
      const m = format.re.exec(rest);
      if (!m) continue;
      const start = m.index + m[1].length;
      if (!best || start < best.start) best = { format, start, end: m.index + m[0].length, inner: m[2] };
    }
    if (!best) {
      nodes.push(rest);
      break;
    }
    if (best.start > 0) nodes.push(rest.slice(0, best.start));
    const { tag: Tag, className, nested } = best.format;
    nodes.push(
      <Tag key={nodes.length} className={className}>
        {nested ? formatWaText(best.inner) : best.inner}
      </Tag>
    );
    rest = rest.slice(best.end);
  }
  return nodes;
}

// Stand-in for the image sent with the template.
function DummyImage() {
  return (
    <svg className="wa-tpl-image" viewBox="0 0 320 168" preserveAspectRatio="xMidYMid slice" role="img" aria-label="Sample image">
      <rect width="320" height="168" fill="#cfe3f5" />
      <circle cx="250" cy="46" r="20" fill="#fde68a" />
      <path d="M0 168 L84 70 L140 126 L196 86 L320 168 Z" fill="#94b8d6" />
      <path d="M0 168 L120 112 L190 148 L250 122 L320 150 L320 168 Z" fill="#6f9bbf" />
    </svg>
  );
}

/** How the template will look to the customer. `buttons` is a list of labels. */
export function TemplatePreview({ headerType, header, mediaUrl, body, footer, buttons = [] }) {
  const type = String(headerType || '').toUpperCase();
  const mediaIcon = MEDIA_ICONS[type];

  return (
    <div className="wa-tpl-chat">
      <div className="wa-tpl-preview">
        {type === 'IMAGE' ? (
          mediaUrl ? <img className="wa-tpl-image" src={mediaUrl} alt="Template header" /> : <DummyImage />
        ) : (
          mediaIcon && (
            <div className="wa-tpl-media">
              <FontAwesomeIcon icon={mediaIcon} />
              <span>{type.toLowerCase()} header</span>
            </div>
          )
        )}
        {!mediaIcon && header && <div className="wa-msg-header">{header}</div>}
        <div className="wa-msg-text">
          {body ? formatWaText(body) : <span className="wa-muted">The body appears here.</span>}
        </div>
        {footer && <div className="wa-msg-footer">{footer}</div>}
        {buttons.length > 0 && (
          <div className="wa-msg-buttons">
            {buttons.map((label, i) => (
              <span key={i} className="wa-msg-button">{label}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_PILLS = {
  APPROVED: 'wa-pill-success',
  REJECTED: 'wa-pill-danger',
  DISABLED: 'wa-pill-danger',
  PENDING: 'wa-pill-warning',
  PAUSED: 'wa-pill-warning',
};

export function TemplateStatus({ status }) {
  const value = String(status || '').toUpperCase();
  return (
    <span className={`wa-pill ${STATUS_PILLS[value] || 'wa-pill-neutral'}`}>
      {value ? value.charAt(0) + value.slice(1).toLowerCase() : 'Unknown'}
    </span>
  );
}

export const buttonLabel = (button) =>
  button?.text || (String(button?.type || '').toUpperCase() === 'COPY_CODE' ? 'Copy code' : button?.type || 'Button');
